import React, { useState, useRef } from 'react';
import { css } from '@emotion/react';
import { palette } from '@leafygreen-ui/palette';

// Components
import { Body } from '@leafygreen-ui/typography';
import AutoAdvanceInputs from 'js/common/components/AutoAdvanceInputs';
import { OktaMfaFactor } from 'js/common/types/accountMultiFactorAuth';

// api
import * as api from 'js/common/services/api';

// utils
import { isLastAuthExpired } from 'js/common/utils/multiFactorAuthUtils';

// types
import { Search } from 'js/auth/types/search';

const activationErrorMessageStyles = css({
  marginTop: '16px',
  marginBottom: '10px',
  color: palette.red.base,
});

type CodeIcon = '' | 'spinner' | 'complete';
type InputType = 'voiceCode' | 'code';

interface VerifyPasscodeInputProps {
  factor?: OktaMfaFactor;
  clientState?: Search;
  onSuccess: Function;
  isActivation?: boolean;
  inputType: InputType;
}

export default function VerifyPasscodeInput({
  factor,
  clientState = {},
  onSuccess,
  isActivation = false,
  inputType,
}: VerifyPasscodeInputProps) {
  const [activationErrorMessage, setActivationErrorMessage] = useState('');
  const [codeIcon, setCodeIcon] = useState<CodeIcon>('');
  const autoAdvanceInputsRef = useRef<AutoAdvanceInputs>(null);

  const { stateToken } = clientState;
  const isAuthFlow = !!stateToken;

  const onComplete = async ({ value: authCode }) => {
    // If the factor isn't loaded yet, just throw an error and reset input
    if (!factor) {
      // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
      autoAdvanceInputsRef.current.reset();
      setActivationErrorMessage("Your passcode doesn't match our records. Please try again.");
      return;
    }

    setCodeIcon('spinner');
    try {
      let response;

      if (isAuthFlow) {
        response = await api.auth.verifyAuthMfa({
          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ factorType: FactorType; factor... Remove this comment to see the full error message
          factorType: factor.factorType,
          factorId: factor.id,
          passcode: authCode,
          stateToken,
          clientState,
        });
      } else if (isActivation) {
        response = await api.accountMultiFactorAuth.activateFactor({
          factorType: factor.factorType,
          factorId: factor.id,
          passcode: authCode,
        });
      } else {
        response = await api.accountMultiFactorAuth.verifyFactor({
          factorType: factor.factorType,
          factorId: factor.id,
          passcode: authCode,
        });
      }

      setCodeIcon('complete');
      setActivationErrorMessage('');
      onSuccess(response);
    } catch ({ errorCode }) {
      if (!isAuthFlow && isLastAuthExpired(errorCode)) {
        window.location.assign('/account/profile/security?expired=true');
      } else {
        // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
        autoAdvanceInputsRef.current.reset();
        setCodeIcon('');
        setActivationErrorMessage("Your passcode doesn't match our records. Please try again.");
      }
    }
  };

  return (
    <>
      <AutoAdvanceInputs
        inputsType={inputType}
        onComplete={onComplete}
        icon={codeIcon}
        ref={autoAdvanceInputsRef}
        isInvalid={!!activationErrorMessage}
      />
      {activationErrorMessage && (
        <Body data-testid="error" css={activationErrorMessageStyles}>
          {activationErrorMessage}
        </Body>
      )}
    </>
  );
}
